// Main menu (GDD §44.5): full out-of-game hub — play, deck editor, profile, settings.
// Layout follows the owner mockup: title + tagline, ornate PLAY plaque, square icon buttons.
// Play mode selection now lives behind the main PLAY plaque.

import { useState } from 'react';
import { loadProfile } from '../../app/persistence';
import { CURRENT_VERSION } from '../../content/changelog';
import menuBg from '../assets/menu-bg.png';
import { ChangelogWindow } from './Changelog';

const VERSION = `v${CURRENT_VERSION} — four-faction development build`;
const DISCORD_URL = 'https://discord.gg/P7zSEQaHKD';

function IconDiscord() {
  return (
    <svg viewBox="0 0 24 24" width="38" height="38" aria-hidden="true">
      <path
        fill="currentColor"
        d="M19.54 5.33A16.8 16.8 0 0 0 15.42 4l-.5 1.02a15.6 15.6 0 0 0-5.84 0L8.58 4a16.8 16.8 0 0 0-4.12 1.33C1.85 9.23 1.15 13.04 1.5 16.8a16.7 16.7 0 0 0 5.06 2.57l1.23-1.68c-.68-.25-1.33-.57-1.94-.95l.47-.36c3.74 1.75 7.8 1.75 11.5 0 .16.13.32.25.48.36-.61.38-1.26.7-1.94.95l1.23 1.68a16.7 16.7 0 0 0 5.06-2.57c.41-4.37-.7-8.14-3.11-11.47ZM8.72 15.05c-1.1 0-2-1.02-2-2.27s.88-2.27 2-2.27c1.12 0 2.02 1.02 2 2.27 0 1.25-.88 2.27-2 2.27Zm6.56 0c-1.1 0-2-1.02-2-2.27s.88-2.27 2-2.27c1.12 0 2.02 1.02 2 2.27 0 1.25-.88 2.27-2 2.27Z"
      />
    </svg>
  );
}

function IconCards() {
  return (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="5.5" y="4.5" width="9" height="13" rx="1" transform="rotate(-8 10 11)" />
      <rect x="10" y="6" width="9" height="13" rx="1" transform="rotate(8 14.5 12.5)" />
    </svg>
  );
}

function IconBanner() {
  return (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      <path d="M7 4h10v12l-5 4-5-4z" />
      <path d="M12 8v5" />
    </svg>
  );
}

function IconGear() {
  return (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" />
    </svg>
  );
}

export function MainMenu({
  problems,
  onPlay,
  onTutorial,
  onDeckEditor,
  onProfile,
  onSettings,
}: {
  problems: string[];
  onPlay: () => void;
  onTutorial: () => void;
  onDeckEditor: () => void;
  onProfile: () => void;
  onSettings: () => void;
}) {
  const [showControls, setShowControls] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const results = loadProfile();
  const wins = results.filter((r) => r.result === 'victory').length;
  const losses = results.filter((r) => r.result === 'defeat').length;

  return (
    <div className="menu mainmenu screen-in" style={{ background: `#0a0c12 url(${menuBg}) center / cover no-repeat` }}>
      <a
        className="discord-cta"
        href={DISCORD_URL}
        target="_blank"
        rel="noreferrer"
        aria-label="Join our Discord"
      >
        <span className="discord-sparkle discord-sparkle-one" aria-hidden="true">✦</span>
        <span className="discord-sparkle discord-sparkle-two" aria-hidden="true">✧</span>
        <span className="discord-sparkle discord-sparkle-three" aria-hidden="true">·</span>
        <span className="discord-icon-wrap">
          <IconDiscord />
        </span>
        <span className="discord-label">Join our Discord</span>
      </a>
      <div className="titleblock">
        <h1>SIGILFRONT</h1>
        <div className="tagline">Four Factions. One Front.</div>
      </div>
      {problems.length > 0 && (
        <div style={{ color: '#ff6a5e', fontSize: 13, maxWidth: 640, textShadow: '0 1px 4px #000' }}>
          Content validation: {problems.join('; ')}
        </div>
      )}
      <button className="playbtn" onClick={onPlay}>
        <span className="playbtn-inner">PLAY</span>
      </button>
      <div className="menusquares">
        <button className="squarebtn" onClick={onDeckEditor}>
          <IconCards />
          <span>Decks</span>
        </button>
        <button className="squarebtn" onClick={onProfile}>
          <IconBanner />
          <span>Battle Record</span>
        </button>
        <button className="squarebtn" onClick={onSettings}>
          <IconGear />
          <span>Settings</span>
        </button>
      </div>
      {results.length > 0 && (
        <div className="menurecord">
          record {wins}–{losses}
        </div>
      )}
      <div className="menulinks">
        <button className="linkbtn tutoriallink" onClick={onTutorial}>
          ✦ Tutorial
        </button>
        <button className="linkbtn" onClick={() => setShowControls((s) => !s)}>
          {showControls ? '▾ Hide controls' : '▸ Controls reference'}
        </button>
        <button className="linkbtn" onClick={() => setShowChangelog(true)}>
          📜 What’s New
        </button>
      </div>
      {showControls && (
        <div className="controlsref">
          <div>Cards: one row of 10 · hotkeys 1–9 then 0</div>
          <div>Arrow keys pan · wheel zoom · left-click select, drag to box-select</div>
          <div>
            Right-click: context order (attack / capture / move / repair / claim ruin / mount wall or
            boss / set a selected building or Core rally flag)
          </div>
          <div>A attack · C capture · D defend · P patrol · R raid · E escort · W withdraw</div>
          <div>H recall/resume the selected zone&apos;s workers · F focus Core · Space pause · Esc cancel</div>
        </div>
      )}
      <button className="menufooter" onClick={() => setShowChangelog(true)} title="View changelog">
        {VERSION}
      </button>
      {showChangelog && <ChangelogWindow onClose={() => setShowChangelog(false)} />}
    </div>
  );
}
